import { useQuery, useQueryErrorResetBoundary } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { AlertCircle, UserEdit, XClose } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { DrawerHeader } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { Illustration } from "@/components/shared-assets/illustrations";
import { EntityTypeOptionsMenu } from "@/features/entity-types/components/entity-type-options-menu";
import { DeleteOrganizationRoleModal } from "@/features/organization-roles/DeleteOrganizationRoleModal";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import { organizationQueryOptions } from "@/services/organization";

export const TabSchema = z.enum(["details", "permissions", "users"]);
type Tab = z.infer<typeof TabSchema>;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/roles/(roles)/$roleId",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/roles/(roles)/$roleId",
	});
	const [isOpen, setIsOpen] = useState(false);
	// Reset query errors when route changes to prevent remount loops
	const queryErrorResetBoundary = useQueryErrorResetBoundary();

	const currentTab = (location.pathname.split("/").pop() as Tab) || "details";

	const {
		data: role,
		isLoading,
		isError: hasError,
	} = useQuery(
		organizationQueryOptions.systemOrganizationRoles.detail(params.roleId),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteOrganizationRole",
			entityType: "OrganizationRole",
			entityId: params.roleId,
		}),
		enabled: Boolean(params.roleId),
		// Role assignments change on the Users page, so never reuse an earlier result.
		staleTime: 0,
		gcTime: 0,
	});
	const canDelete = canDeleteQuery.data === true;
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);

	// Open drawer when component mounts (route is accessed)
	useEffect(() => {
		setIsOpen(true);
	}, []);

	// Reset query error boundary on mount to prevent remount loops
	useEffect(() => {
		queryErrorResetBoundary.reset();
	}, [queryErrorResetBoundary]);

	// Handle drawer close - navigate back to parent route
	const handleClose = () => {
		setIsOpen(false);
		// Delay navigation to allow exit animation to complete (200ms)
		setTimeout(() => {
			navigate({
				to: "/org-admin/$orgId/roles",
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	// Handle drawer state change
	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			// If drawer is being closed, navigate back
			handleClose();
		}
	};

	const title = role?.name || t("admin.roles.drawer.title");
	const tabs = [
		{ label: t("admin.roles.drawer.details"), id: "details" },
		{ label: t("admin.roles.drawer.permissions"), id: "permissions" },
	];

	return (
		<>
			<Drawer
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				width="w-[570px]"
				backdropPreset="transparent"
				dialogClassName="gap-0 overflow-hidden"
			>
				{({ close }) => (
					<div className="flex h-full min-h-0 w-full flex-col">
						<DrawerHeader
							type="default"
							title={title}
							featuredIcon={UserEdit}
							featuredIconColor="gray"
							showSpacer={false}
							metadata={[{ label: t("admin.roles.drawer.description") }]}
							overflowControl={
								<>
									<EntityTypeOptionsMenu
										deleteLabel={
											canDelete ? t("admin.roles.delete.action") : undefined
										}
										onDelete={
											canDelete ? () => setIsDeleteOpen(true) : undefined
										}
									/>
									<IconButton
										aria-label={t("common.actions.close")}
										hierarchy="secondary"
										size="sm"
										shape="square"
										icon={XClose}
										onPress={() => {
											close();
											handleClose();
										}}
									/>
								</>
							}
						/>
						{isLoading ? (
							<div className="flex min-h-0 flex-1 items-center justify-center">
								<LoadingIndicator type="line-simple" size="xl" supportingText />
							</div>
						) : hasError ? (
							<div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4">
								<Illustration type="cloud" size="sm">
									<div className="bg-error-500 rounded-full p-3">
										<AlertCircle className="size-6" />
									</div>
								</Illustration>
								<div className="flex flex-col items-center justify-center pt-4">
									<p className="text-gray-900 font-semibold text-md">
										{t("admin.organizations.errors.errorLoadingPage")}
									</p>
									<div className="w-[60%]">
										<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
											{t("admin.organizations.errors.errorLoadingContent")}
										</p>
									</div>
								</div>
							</div>
						) : (
							<>
								<NativeSelect
									aria-label={t("admin.roles.drawer.rollTabs")}
									value={currentTab}
									onChange={(event) =>
										navigate({
											to: `/org-admin/$orgId/roles/$roleId/${event.target.value}`,
											params: { roleId: params.roleId },
										})
									}
									options={tabs.map((tab) => ({
										label: String(tab.label),
										value: tab.id,
									}))}
									className="mx-6 mt-3 w-80 md:hidden"
								/>
								<Tabs
									selectedKey={currentTab}
									onSelectionChange={(event) =>
										navigate({
											to: `/org-admin/$orgId/roles/$roleId/${event}`,
											params: { roleId: params.roleId },
										})
									}
									className="w-full max-md:hidden"
								>
									<div className="flex h-12 w-full shrink-0 flex-col items-stretch border-b border-secondary bg-primary px-6">
										<Tabs.List
											type="underline"
											items={tabs}
											className="h-full w-full items-stretch gap-3 border-b-0"
										>
											{(tab) => (
												<Tabs.Item {...tab} className="h-full items-end" />
											)}
										</Tabs.List>
									</div>
								</Tabs>
								<Drawer.ScrollBody showPlaceholder={false}>
									<TabContentWrapper
										tabKey={currentTab}
										className="flex h-full min-h-0 w-full flex-col"
									>
										<Outlet />
									</TabContentWrapper>
								</Drawer.ScrollBody>
							</>
						)}
					</div>
				)}
			</Drawer>
			<DeleteOrganizationRoleModal
				isOpen={isDeleteOpen}
				onOpenChange={setIsDeleteOpen}
				organizationId={params.orgId}
				roleId={params.roleId}
				roleName={role?.name ?? ""}
				onDeleted={handleClose}
			/>
		</>
	);
}
